import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@mezzodevelopment/mezzo-ui/auth";
import AppLayout from "./components/layout/AppLayout";
import Dashboard from "./pages/Dashboard";
import CreditEmulator from "./pages/credit/CreditEmulator";
import CreditAccounts from "./pages/credit/CreditAccounts";
import CreditReports from "./pages/credit/CreditReports";
import Underwrites from "./pages/underwrite/Underwrites";
import Billing from "./pages/billing/Billing";
import Clients from "./pages/client/Clients";
import EditClient from "./pages/client/EditClient";
import EditBranch from "./pages/branch/EditBranch";
import UserAccounts from "./pages/user-accounts/UserAccounts";
import EditUserAccount from "./pages/user-accounts/EditUserAccount";
import CreditReportingAgencies from "./pages/credit/CreditReportingAgencies";
import EditCreditReportingAgency from "./pages/credit/EditCreditReportingAgency";
import ConnectionSettings from "./pages/connection-settings/ConnectionSettings";
import { AuthCallback } from "./pages/authCallback/AuthCallback";
import SecureRoute from "./components/auth/secureRoute";
import Login from "./pages/Login";
import LoadingPage from "./pages/Loading";
import ProductDashboard from "./pages/ProductDashboard";
import ViewEmailTemplate from "./pages/email/ViewEmailTemplate";
// OrganizationalUnits import removed - feature deprecated
import Unauthorized from "./pages/errors/Unauthorized";
import { Tenants } from "./pages/tenant/Tenants";
import { WebhookSubscriptions } from "./pages/webhook-subscriptions/WebhookSubscriptions";
import { CustomFields } from "./pages/custom-fields/CustomFields";
import { WaterfallsContainer } from "./pages/waterfalls/WaterfallsContainer";
import { WaterfallEditorContainer } from "./pages/waterfall-editor/WaterfallEditorContainer";
import { IntegrationType } from "./models/waterfall";
import { FeesContainer } from "./pages/fees/FeesContainer";
import { Argyle } from "./pages/vox/argyle";
import { Truv } from "./pages/vox/truv";
import { TruvEmbed } from "./pages/vox/truv/embed";
import { FeeConfigurationDetailsContainer } from "./pages/fees/FeeConfigurationDetailsContainer";
import HudLinesContainer from "./pages/fees/HudLinesContainer";
import FeeTracingSearch from "./pages/fee-tracing/FeeTracingSearch";
import FeeTracingDetails from "./pages/fee-tracing/FeeTracingDetails";
import VoxTracingSearch from "./pages/vox-tracing/VoxTracingSearch";
import WaterfallExecutionDetail from "./pages/vox-tracing/WaterfallExecutionDetail";
import VoxOrderDetail from "./pages/vox-tracing/VoxOrderDetail";

const client = new QueryClient();

const AuthProviderLayout = () => (
  <AuthProvider
    config={{
      baseURL:
        process.env.REACT_APP_API_BASE_URL || "https://api.teammezzo.com",
      redirectUri: "/auth/callback",
      loginRedirectRoute: "/dashboard",
      logoutRedirectRoute: "/login",
    }}
  >
    <Outlet />
  </AuthProvider>
);

function App() {
  return (
    <QueryClientProvider client={client}>
      <Routes>
        {/* Public routes */}
        <Route path="/vox/argyle/iframe" element={<Argyle />} />
        <Route path="/vox/truv/iframe" element={<Truv />} />
        <Route path="/vox/truv/embed" element={<TruvEmbed />} />
        {/* Protected routes */}
        <Route element={<AuthProviderLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="loading" element={<LoadingPage />} />
          <Route path="401" element={<Unauthorized />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route element={<AppLayout />}>
            <Route path="" element={<Navigate to="/dashboard" />} />
            <Route
              path="dashboard"
              element={
                <SecureRoute>
                  <Dashboard />
                </SecureRoute>
              }
            />
            {/* ── Product Dashboards ── */}
            <Route
              path="products/credit"
              element={
                <SecureRoute>
                  <ProductDashboard product="credit" />
                </SecureRoute>
              }
            />
            <Route
              path="products/fees"
              element={
                <SecureRoute>
                  <ProductDashboard product="fees" />
                </SecureRoute>
              }
            />
            <Route
              path="products/vox"
              element={
                <SecureRoute>
                  <ProductDashboard product="vox" />
                </SecureRoute>
              }
            />
            <Route
              path="products/auw"
              element={
                <SecureRoute>
                  <ProductDashboard product="auw" />
                </SecureRoute>
              }
            />
            <Route
              path="products/price"
              element={
                <SecureRoute>
                  <ProductDashboard product="price" />
                </SecureRoute>
              }
            />
            <Route
              path="credit/emulator"
              element={
                <SecureRoute>
                  <CreditEmulator />
                </SecureRoute>
              }
            />
            <Route
              path="credit/accounts"
              element={
                <SecureRoute>
                  <CreditAccounts />
                </SecureRoute>
              }
            />
            <Route
              path="credit/transactions"
              element={
                <SecureRoute>
                  <CreditReports />
                </SecureRoute>
              }
            />
            <Route
              path="underwrite"
              element={
                <SecureRoute>
                  <Underwrites />
                </SecureRoute>
              }
            />
            <Route
              path="billing"
              element={
                <SecureRoute>
                  <Billing />
                </SecureRoute>
              }
            />
            <Route path="fee-tracing">
              <Route
                index={true}
                element={
                  <SecureRoute>
                    <FeeTracingSearch />
                  </SecureRoute>
                }
              />
              <Route
                path=":transactionId"
                element={
                  <SecureRoute>
                    <FeeTracingDetails />
                  </SecureRoute>
                }
              />
            </Route>

            <Route path="vox-tracing">
              <Route
                index={true}
                element={
                  <SecureRoute>
                    <VoxTracingSearch />
                  </SecureRoute>
                }
              />
              <Route
                path="waterfall/:waterfallGuid"
                element={
                  <SecureRoute>
                    <WaterfallExecutionDetail />
                  </SecureRoute>
                }
              />
              <Route
                path="order/:voxOrderGuid"
                element={
                  <SecureRoute>
                    <VoxOrderDetail />
                  </SecureRoute>
                }
              />
            </Route>

            <Route path="clients">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <Clients />
                  </SecureRoute>
                }
              />
              <Route
                path="new"
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <EditClient />
                  </SecureRoute>
                }
              />
              <Route
                path=":clientId"
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <EditClient />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="branches">
              <Route
                path=":clientId"
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <EditBranch />
                  </SecureRoute>
                }
              />
              <Route
                path=":clientId/:branchId"
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <EditBranch />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="user-accounts">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <UserAccounts />
                  </SecureRoute>
                }
              />
              <Route
                path="new"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <EditUserAccount />
                  </SecureRoute>
                }
              />
              <Route
                path=":userId"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <EditUserAccount />
                  </SecureRoute>
                }
              />
            </Route>
            {/* Organizational Units route removed - feature deprecated */}
            <Route path="connection-settings">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <ConnectionSettings />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="credit-reporting-agencies">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <CreditReportingAgencies />
                  </SecureRoute>
                }
              />
              <Route
                path="new"
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <EditCreditReportingAgency />
                  </SecureRoute>
                }
              />
              <Route
                path=":creditReportingAgencyId"
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <EditCreditReportingAgency />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="email-template">
              <Route
                path=":emailType"
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <ViewEmailTemplate />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="tenants">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin"]}>
                    <Tenants />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="waterfalls">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallsContainer />
                  </SecureRoute>
                }
              />
              <Route
                path="new"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer />
                  </SecureRoute>
                }
              />
              <Route
                path="edit/:waterfallId"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer />
                  </SecureRoute>
                }
              />
            </Route>
            {/* Product-filtered Waterfall routes */}
            <Route path="credit/waterfalls">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallsContainer
                      integrationType="Credit"
                      title="Credit Waterfalls"
                      basePath="/credit/waterfalls"
                    />
                  </SecureRoute>
                }
              />
              <Route
                path="new"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer
                      basePath="/credit/waterfalls"
                      defaultIntegrationType={IntegrationType.Credit}
                    />
                  </SecureRoute>
                }
              />
              <Route
                path="edit/:waterfallId"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer
                      basePath="/credit/waterfalls"
                      defaultIntegrationType={IntegrationType.Credit}
                    />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="auw/waterfalls">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallsContainer
                      integrationType="Underwrite"
                      title="AUW Waterfalls"
                      basePath="/auw/waterfalls"
                    />
                  </SecureRoute>
                }
              />
              <Route
                path="new"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer
                      basePath="/auw/waterfalls"
                      defaultIntegrationType={IntegrationType.Underwrite}
                    />
                  </SecureRoute>
                }
              />
              <Route
                path="edit/:waterfallId"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer
                      basePath="/auw/waterfalls"
                      defaultIntegrationType={IntegrationType.Underwrite}
                    />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="vox/waterfalls">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallsContainer
                      integrationType="IncomeEmployment"
                      title="VoX Waterfalls"
                      basePath="/vox/waterfalls"
                    />
                  </SecureRoute>
                }
              />
              <Route
                path="new"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer
                      basePath="/vox/waterfalls"
                      defaultIntegrationType={IntegrationType.IncomeEmployment}
                    />
                  </SecureRoute>
                }
              />
              <Route
                path="edit/:waterfallId"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer
                      basePath="/vox/waterfalls"
                      defaultIntegrationType={IntegrationType.IncomeEmployment}
                    />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="fees">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <FeesContainer />
                  </SecureRoute>
                }
              />
              <Route
                path="create"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <FeeConfigurationDetailsContainer />
                  </SecureRoute>
                }
              />
              <Route
                path="edit/:feeConfigurationId"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <FeeConfigurationDetailsContainer />
                  </SecureRoute>
                }
              />
              <Route
                path="hud-lines"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <HudLinesContainer />
                  </SecureRoute>
                }
              />
              <Route
                path="waterfalls"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallsContainer
                      integrationType="Fees"
                      title="Fees Waterfalls"
                      basePath="/fees/waterfalls"
                    />
                  </SecureRoute>
                }
              />
              <Route
                path="waterfalls/new"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer
                      basePath="/fees/waterfalls"
                      defaultIntegrationType={IntegrationType.Fees}
                    />
                  </SecureRoute>
                }
              />
              <Route
                path="waterfalls/edit/:waterfallId"
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WaterfallEditorContainer
                      basePath="/fees/waterfalls"
                      defaultIntegrationType={IntegrationType.Fees}
                    />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="webhook-subscriptions">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <WebhookSubscriptions />
                  </SecureRoute>
                }
              />
            </Route>
            <Route path="custom-fields">
              <Route
                index={true}
                element={
                  <SecureRoute scopes={["SuperAdmin", "Admin"]}>
                    <CustomFields />
                  </SecureRoute>
                }
              />
            </Route>
          </Route>
        </Route>
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
